import { useEffect, useState } from "react";
import { API, useCurrency } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { CheckCircle, XCircle, Clock, Wallet, AlertTriangle, UserX } from "lucide-react";
import { toast } from "sonner";

export default function AdminRefunds() {
  const { symbol } = useCurrency();
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [tab, setTab] = useState("requests");

  useEffect(() => { fetchRefunds(); }, []);

  const fetchRefunds = async () => {
    try {
      const res = await fetch(`${API}/admin/refunds`, { credentials: "include" });
      if (res.ok) setRefunds(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleApprove = async (refundId) => {
    setProcessing(refundId);
    try {
      const res = await fetch(`${API}/admin/refunds/${refundId}/approve`, { method: "PUT", credentials: "include" });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Failed to approve"); return; }
      toast.success("Refund approved and wallet credited");
      fetchRefunds();
    } catch { toast.error("Failed to approve refund"); }
    finally { setProcessing(null); }
  };

  const handleReject = async (refundId) => {
    if (!window.confirm("Reject this refund request?")) return;
    setProcessing(refundId);
    try {
      const res = await fetch(`${API}/admin/refunds/${refundId}/reject`, { method: "PUT", credentials: "include" });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Failed to reject"); return; }
      toast.success("Refund rejected");
      fetchRefunds();
    } catch { toast.error("Failed to reject refund"); }
    finally { setProcessing(null); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  const adminRefunds = refunds.filter(r => !r.cancelled_by_user);
  const userCancelled = refunds.filter(r => r.cancelled_by_user);
  const pendingCount = adminRefunds.filter(r => r.status === "pending").length;
  const displayList = tab === "requests" ? adminRefunds : userCancelled;

  return (
    <AdminLayout>
      <div className="animate-fade-in max-w-full overflow-x-hidden" data-testid="admin-refunds">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Refunds</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {pendingCount} pending · {refunds.length} total
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 flex-wrap" data-testid="refund-tabs">
          <button
            onClick={() => setTab("requests")}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${tab === "requests" ? "bg-green-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid="tab-requests"
          >
            Refund Requests {pendingCount > 0 && <span className="ml-1.5 bg-white/20 text-white px-1.5 py-0.5 rounded-full text-xs">{pendingCount}</span>}
          </button>
          <button
            onClick={() => setTab("cancelled")}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${tab === "cancelled" ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid="tab-cancelled"
          >
            Cancelled by User {userCancelled.length > 0 && <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded-full text-xs">{userCancelled.length}</span>}
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">{[1, 2].map((i) => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>
        ) : displayList.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            {tab === "requests" ? <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-4" /> : <UserX className="w-12 h-12 text-gray-300 mx-auto mb-4" />}
            <p className="text-gray-500">{tab === "requests" ? "No refund requests" : "No user-cancelled orders"}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayList.map((ref) => (
              <div key={ref.refund_id} className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5" data-testid={`refund-card-${ref.refund_id}`}>
                <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                      <span className="font-bold text-gray-900 text-sm sm:text-base break-all">Order #{ref.order_number}</span>
                      <StatusBadge status={ref.status} cancelledByUser={ref.cancelled_by_user} />
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 break-all">{ref.user_name} ({ref.user_email})</p>
                    <p className="text-base sm:text-lg font-bold text-green-700 mt-1">{symbol}{ref.amount}</p>
                    {ref.reason && (
                      <div className="flex items-start gap-1 mt-2 text-xs sm:text-sm text-gray-500">
                        <AlertTriangle size={14} className="text-orange-400 mt-0.5 flex-shrink-0" />
                        <span className="break-words">{ref.reason}</span>
                      </div>
                    )}
                    <p className="text-[10px] text-gray-500 mt-2 break-words">
                      {ref.cancelled_by_user ? "Cancelled by user" : `Requested by ${ref.requested_by_role || "admin"}`} · {formatDate(ref.created_at)}
                    </p>
                  </div>
                  {ref.status === "pending" && !ref.cancelled_by_user && (
                    <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
                      <Button size="sm" variant="outline" onClick={() => handleReject(ref.refund_id)} disabled={processing === ref.refund_id} className="text-red-600 border-red-200 hover:bg-red-50 flex-1 sm:flex-none" data-testid={`reject-refund-${ref.refund_id}`}>
                        <XCircle size={14} className="mr-1" /> Reject
                      </Button>
                      <Button size="sm" onClick={() => handleApprove(ref.refund_id)} disabled={processing === ref.refund_id} className="bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-none" data-testid={`approve-refund-${ref.refund_id}`}>
                        <CheckCircle size={14} className="mr-1" /> Approve
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function StatusBadge({ status, cancelledByUser }) {
  if (cancelledByUser) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        <UserX size={12} /> Cancelled by User
      </span>
    );
  }
  const styles = {
    pending: "bg-yellow-100 text-yellow-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
  };
  const icons = {
    pending: <Clock size={12} />,
    approved: <CheckCircle size={12} />,
    rejected: <XCircle size={12} />,
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${styles[status] || "bg-gray-100 text-gray-600"}`}>
      {icons[status]} {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
